import { useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { useVersionCheck } from "@/hooks/useVersionCheck";

/**
 * Non-blocking banner that appears when a newer deploy is detected.
 * Reload button does a full `location.reload()` to pull the new bundle;
 * dismiss button hides the banner for the rest of the session (no
 * persisted dismissal — next route load re-evaluates the version check
 * via the hook).
 *
 * Visual is intentionally low-contrast and unobtrusive: the user is
 * mid-task, we're not blocking them. Gmail / Linear / Notion all
 * follow this convention.
 */
export function VersionUpdateBanner() {
  const { staleBuildId } = useVersionCheck();
  const [dismissed, setDismissed] = useState(false);

  if (!staleBuildId || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 flex max-w-sm items-center gap-3 rounded-xl border border-[#4DB8C7]/30 bg-[#151D2E] px-4 py-3 shadow-lg shadow-black/30 sm:bottom-6"
    >
      <RefreshCw className="h-4 w-4 shrink-0 text-[#4DB8C7]" />
      <span className="text-sm text-[#E8EDF2]">
        A new version is available.
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-lg bg-[#4DB8C7] px-3 py-1 text-xs font-semibold text-[#0C1222] transition-colors hover:bg-[#3DA8B7]"
      >
        Reload
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss update notice"
        className="shrink-0 rounded p-1 text-[#7A8FA3] transition-colors hover:bg-[#1E2B3D] hover:text-[#E8EDF2]"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
